import get from 'lodash/get'
import pick from 'lodash/pick'
import forEach from 'lodash/forEach'
import videojs from 'video.js'
import { conditionalError, isUrlInDom, getTypeFromSource, isValidOffset } from './utils'
import './plugins/NFBTheme'
import './plugins/NFBCustomEvents'
import 'video.js/dist/video-js.css'
import './sass/reset.sass'

const isMobileOrTablet = videojs.browser.IS_ANDROID || videojs.browser.IS_IOS

const defaultOptions = {
  source: null, // required
  dvSource: null, // optional

  // inherit from videoJS
  poster: false,
  controls: true,
  autoplay: false,
  loop: false,
  muted: false,
  playbackRates: false,
  preload: 'metadata',
  language: 'en',
  inactivityTimeout: 2000,
  volumePanel: {
    inline: true
  },

  // custom
  vhs: {
    withCredentials: false
  },
  theme: {
    name: 'default',
    accentColor: 'blue',
    buttonOrders: [
      '.vjs-subs-caps-button.vjs-control',
      '.vjs-audio-description-button.vjs-control',
      '.vjs-playback-rate.vjs-control',
      '.vjs-quality-selector.vjs-control',
      '.vjs-fullscreen-control.vjs-control',
      '.vjs-chromecast-button.vjs-control'
    ]
  },
  offset: {
    start: null, // in seconds
    end: null // in seconds
  },
  restartFromBeginning: false,
  seekButtons: {
    forward: false,
    backward: isMobileOrTablet ?? false,
    seekDuration: 10
  },
  hotKeys: {
    autofocus: false
  },
  stat: false,
  persistOptions: {
    persistCC: true,
    persistVolume: true
  },
  ratings: {
    message: null
  },
  chromecast: {
    addButtonToControlBar: true
  },
  airPlay: {
    addButtonToControlBar: true
  },
  vr: false, // options here : https://github.com/videojs/videojs-vr#options
  trickplay: true,
  overlay: null,
  disableResolutionsFn: false, // should be false or a function with value parameter
  ready: null, // callback
  logLevel: 'warn',
  triggerWarnings: null
}

export default class NFBWebPlayer {
  constructor ({ el, options }) {
    this.containerEl = this.getContainerEl(el)
    this.player = null
    this.sprites = []

    this.options = { ...defaultOptions, ...options }
    videojs.log.level(this.options.logLevel)

    // We want to wait offset plugin to be loaded before autoplay
    if (this.options.autoplay && (this.options.offset.start || this.options.offset.end)) {
      this.options.autoplay = false
      this.options.offset.autoplay = true
    }

    const inheritVideoJSOptions = pick(this.options, [
      'poster',
      'controls',
      'autoplay',
      'loop',
      'muted',
      'playbackRates',
      'preload',
      'language',
      'inactivityTimeout'
    ])

    // Hide volume for mobile & tablet
    const volumePanel = isMobileOrTablet
      ? false
      : this.options.volumePanel

    this.videoJsOptions = {
      ...inheritVideoJSOptions,
      techOrder: ['html5'],
      controlBar: {
        muteToggle: false,
        volumeBar: false,
        volumePanel,
        pictureInPictureToggle: false
      },
      html5: {
        vhs: {
          handlePartialData: true,
          limitRenditionByPlayerDimensions: false, // limit quality to maximum player dimension
          enableLowInitialPlaylist: true,
          overrideNative: !videojs.browser.IS_SAFARI, // true causes bug in Safari (cf FRONT-637)
          withCredentials: this.options.vhs.withCredentials
        }
      },
      plugins: {}
    }

    this.checkRequiredOptions()
    this.init()

    return this
  }

  getContainerEl (el) {
    if (el === undefined) {
      throw new Error('Element container needs to be specified.')
    }

    if (typeof el === 'string') {
      const domEl = document.querySelector(el)
      if (domEl === null) {
        throw new Error(`Impossible to find element with selector "${el}"`)
      }

      return domEl
    } else if (el instanceof HTMLElement) {
      return el
    }
  }

  checkRequiredOptions () {
    if (!get(this.options, 'source')) {
      throw new Error('Source needs to be defined.')
    }
  }

  async init () {
    await this.loadLanguage(this.options.language)

    this.videoType = getTypeFromSource(this.options.source)
    videojs.log(`Video Type: ${this.videoType}`)
    switch (this.videoType) {
    case 'mp4':
      await import(/* webpackChunkName: "videojs-mp4-quality-selector" */'@silvermine/videojs-quality-selector')
        .then(m => {
          m.default(videojs)
        })
      break
    case 'hls':
    default:

      await import(/* webpackChunkName: "videojs-contrib-quality-levels" */'videojs-contrib-quality-levels')
        .then(m => {
          if (!videojs.getPlugin('qualityLevels')) {
            videojs.registerPlugin('qualityLevels', m.default)
          }
        })
      await import(/* webpackChunkName: "videojs-hls-quality-selector" */'videojs-hls-quality-selector')
        .then(m => {
          if (!videojs.getPlugin('hlsQualitySelector')) {
            videojs.registerPlugin('hlsQualitySelector', m.default)
          }
        })
      break
    }

    this.initTheme()

    if (this.options.triggerWarnings) {
      await this.initTriggerWarnings()
    }

    if (this.options.dvSource) {
      await this.initAudioDescription()
    }

    if (this.options.chromecast) {
      await this.initChromecast()
    }

    if (this.options.airPlay) {
      await this.initAirPlay()
    }

    if (this.options.overlay) {
      await this.initOverlay()
    }

    if (this.options.restartFromBeginning) {
      await this.initRestartFromBeginning()
    }

    if (this.options.seekButtons?.forward || this.options.seekButtons?.backward) {
      await this.initSeekButtons()
    }

    if (this.options.hotKeys) {
      await this.initHotKeys()
    }

    if (this.options.trickplay && this.videoType === 'hls') {
      await this.initTrickPlay()
    }

    if (this.options.persistOptions) {
      await this.initPersistOptions()
    }

    if (this.options.ratings) {
      await this.initRatings()
    }

    await this.initVideoJs()

    if (isValidOffset(this.options.offset.start) || isValidOffset(this.options.offset.end)) {
      await this.initOffset()
    }

    // Keep stat after offset as it needs the duration to be updated
    if (this.options.stat) {
      await this.initStat()
    }

    if (this.options.vr) {
      await this.initVR()
    }

    return this
  }

  async loadLanguage (locale) {
    videojs.log(`Loading language: ${locale}`)
    conditionalError(
      (locale !== 'en' && locale !== 'fr'),
      `Invalid options.locale = ${locale}. locale should be 'en' or 'fr', defaulting to 'en'`,
      this.options,
      () => (locale = 'en')
    )
    await import(/* webpackChunkName: "videojs-language" */ `./i18n/${locale}_CA.json`).then(json => {
      videojs.addLanguage(locale, json)
    })
  }

  async initAudioDescription () {
    videojs.log('Adding Audio Description')

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbAudioDescription: pick(this.options, ['source', 'dvSource', 'vhs'])
    }

    return import(/* webpackChunkName: "videojs-nfb-audio-description" */ './plugins/NFBAudioDescription')
  }

  async initChromecast () {
    // add Chrome Sender API Library if not loaded https://developers.google.com/cast/docs/chrome_sender
    const castUrl = '//www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1'
    if (!isUrlInDom(castUrl)) {
      // Only load script if not already registered
      videojs.log('Adding Chrome Sender API Library')
      const script = document.createElement('script')
      script.setAttribute('src', castUrl)
      document.body.appendChild(script)
    }

    return import(/* webpackChunkName: "videojs-chromecast" */ '@silvermine/videojs-chromecast').then(m => {
      import('@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css')
      if (!videojs.getPlugin('chromecast')) {
        m.default(videojs, { preloadWebComponents: true })
      }
      this.videoJsOptions.techOrder = ['chromecast', 'html5']
      this.videoJsOptions.plugins = {
        ...this.videoJsOptions.plugins,
        chromecast: this.options.chromecast
      }
    })
  }

  async initAirPlay () {
    videojs.log('Adding Air Play')
    return import(/* webpackChunkName: "videojs-airplay" */ '@silvermine/videojs-airplay').then(m => {
      if (!videojs.getPlugin('airPlay')) {
        m.default(videojs)
      }
      this.videoJsOptions.plugins = {
        ...this.videoJsOptions.plugins,
        airPlay: this.options.airPlay
      }
    })
  }

  async initSeekButtons () {
    videojs.log('Adding Seek Buttons')

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbSeekButtons: this.options.seekButtons
    }

    return import(/* webpackChunkName: "videojs-nfb-seek-buttons" */'./plugins/NFBSeekButtons')
  }

  async initRestartFromBeginning () {
    videojs.log('Adding Restart From Beginning Buttons')

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbRestartFromBeginningButton: this.options.restartFromBeginning
    }

    return import(/* webpackChunkName: "videojs-nfb-restart-from-beginning" */'./plugins/NFBRestartFromBeginning')
  }

  async initHotKeys () {
    videojs.log('Adding Hot Keys')

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbHotKeys: this.options.hotKeys
    }

    return import(/* webpackChunkName: "videojs-nfb-hotkeys" */'./plugins/NFBHotKeys')
  }

  async initTrickPlay () {
    videojs.log('Adding Trick Play')

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbTrickPlay: {
        source: this.options.source
      }
    }
    return await import(/* webpackChunkName: "videojs-nfb-trick-play" */'./plugins/NFBTrickPlay')
  }

  async initPersistOptions () {
    videojs.log('Adding Persist Options')

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbPersistOptions: this.options.persistOptions
    }
    return await import(/* webpackChunkName: "videojs-nfb-persist-options" */'./plugins/NFBPersistOptions')
  }

  async initRatings () {
    videojs.log('Adding Ratings')

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbRatings: this.options.ratings
    }
    return await import(/* webpackChunkName: "videojs-nfb-ratings" */'./plugins/NFBRatings')
  }

  async initTriggerWarnings () {
    videojs.log('Adding Trigger Warnings')

    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbTriggerWarnings: this.options.triggerWarnings
    }
    return import(/* webpackChunkName: "videojs-nfb-trigger-warnings" */'./plugins/NFBTriggerWarnings')
  }

  async initOverlay () {
    videojs.log('Adding Overlay')

    // Cascade language
    this.options.overlay.language = this.options.language

    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbOverlay: this.options.overlay
    }

    return import(/* webpackChunkName: "videojs-nfb-overlay" */'./plugins/NFBOverlay')
  }

  initTheme () {
    // Update plugins option
    this.videoJsOptions.plugins = {
      ...this.videoJsOptions.plugins,
      nfbTheme: this.options.theme
    }
  }

  async initVideoJs () {
    videojs.log('Create videojs player', this.videoJsOptions)
    this.player = videojs(this.containerEl, this.videoJsOptions)

    switch (this.videoType) {
    case 'hls':
      this.player.src({
        src: this.options.source,
        type: 'application/x-mpegURL',
        withCredentials: this.options.vhs.withCredentials
      })
      // WARN: player.tech().hls is deprecated: https://github.com/chrisboustead/videojs-hls-quality-selector/issues/78
      this.player.hlsQualitySelector({
        displayCurrentQuality: true
      })

      break

    case 'mp4':
      this.player.addClass('vjs-mp4-source')
      this.player.src(this.options.source)
      this.player.controlBar.addChild('QualitySelector')
      break

    default:
      throw new Error('Video type has to be HLS or MP4')
    }

    this.player.addClass(`vjs-language-${this.options.language}`)

    if (typeof (this.options.disableResolutionsFn) === 'function') {
      this.player.on('loadedmetadata', this.applyDisableResolutions.bind(this))
    }

    if (this.options.ready && typeof this.options.ready === 'function') {
      const expose = await this.initExpose()
      this.player.ready(() => {
        videojs.log('ready')
        this.options.ready(expose)
      })
    }

    this.player.nfbCustomEvents()
  }

  applyDisableResolutions () {
    try {
      videojs.log('Disable resolutions from custom function (kind of hacky ¯\_(ツ)_/¯)') // eslint-disable-line
      const hlsQualitySelectorMenuItems = this.player.hlsQualitySelector._qualityButton.items
      forEach(hlsQualitySelectorMenuItems, menuItem => {
        if (this.options.disableResolutionsFn(menuItem.item)) {
          menuItem.hide()
        }
      })
    } catch (error) {
      videojs.log.warn(`Error when trying to disable resolution: ${error.message}`)
    }
  }

  async initVR () {
    videojs.log('Loading videojs-vr plugin')
    return await import(/* webpackChunkName: "videojs-vr" */ 'videojs-vr/dist/videojs-vr.min.js').then(m => {
      if (!videojs.getPlugin('vr')) {
        videojs.registerPlugin('vr', m.default)
      }
      videojs.log('Video 360 is active')
      videojs.Hls.MAX_GOAL_BUFFER_LENGTH = 30
      videojs.Hls.GOAL_BUFFER_LENGTH = 15
      this.player.mediainfo = this.player.mediainfo || {}
      this.player.vr({ projection: this.options.vr.projection })
      videojs.log(`Video 360 projection type: ${this.options.vr.projection}`)
    })
  }

  async initOffset () {
    videojs.log('Loading videojs-offset plugin')
    return await import(/* webpackChunkName: "videojs-offset" */ 'videojs-offset').then(m => {
      if (!videojs.getPlugin('offset')) {
        videojs.registerPlugin('offset', m.default)
      }
      const start = isValidOffset(this.options.offset.start) ? this.options.offset.start : 0
      const end = isValidOffset(this.options.offset.end) ? this.options.offset.end : 0
      videojs.log(`Offset starts at ${start} and ends at ${end}`)
      this.player.offset({
        start: start,
        end: end,
        restart_beginning: true
      })
      if (this.options.offset.autoplay) {
        this.player.on('loadeddata', () => {
          this.player.play()
        })
      }
    })
  }

  async initStat () {
    videojs.log('Adding Stat')
    return await import(/* webpackChunkName: "videojs-nfb-stat" */'./plugins/NFBStat').then(m => {
      if (!videojs.getPlugin('nfbStat')) {
        videojs.registerPlugin('nfbStat', m.default)
      }
      this.player.nfbStat(this.options.stat)
    })
  }

  async initExpose () {
    videojs.log('Adding Expose')
    return await import(/* webpackChunkName: "videojs-nfb-expose" */'./NFBExpose').then(m => {
      // eslint-disable-next-line
      return new m.default(this.player) 
    })
  }

  destroy () {
    if (this.player) {
      this.player.dispose()
    }
    this.player = null
  }
}
